import { useMemo } from 'react';
import { PlayerLite } from '../../../api/player/model';
import { Game } from '../../../api/games/model';

export interface GameStatisticsResult {
  playerStatistics: GameStatisticsPlayerResult[];
  totalGamesPlayed: number;
}

export type StatisticUnit = 'number' | 'percentage' | 'integer';

export type Statistic = { value: number; rank: number; unit: StatisticUnit };

export interface GameStatisticsPlayerResult {
  player: PlayerLite;
  gamesPlayed: Statistic;
  gamesWon: Statistic;
  winPercentage: Statistic;
  numberOfChallenges: Statistic; // New property
  numberOfDefenses: Statistic; // New property
}

export const useGameStatistics = (
  games: Game[],
  fromDate?: Date,
  toDate?: Date,
  includeDeletedPlayers?: boolean
): GameStatisticsResult => {
  return useMemo(() => {
    const playerGameCounts: Record<string, number> = {};
    const playerWinCounts: Record<string, number> = {};
    const playerChallengeCounts: Record<string, number> = {}; // Count of challenges
    const playerDefenseCounts: Record<string, number> = {}; // Count of defenses
    const playersMap: Record<string, PlayerLite> = {};
    const filteredGames = games.filter((game) => {
      const gameTimestamp = game.gameResultTimestamp;
      if (fromDate && gameTimestamp < fromDate) {
        return false;
      }
      if (toDate && gameTimestamp > toDate) {
        return false;
      }
      return true;
    });

    filteredGames.forEach((game) => {
      const { challenger, defender, winner } = game;
      const challengerId = challenger.id;
      const defenderId = defender.id;
      const winnerId = winner?.id;

      playersMap[challengerId] = challenger;
      playersMap[defenderId] = defender;

      // Increment game counts
      playerGameCounts[challengerId] = (playerGameCounts[challengerId] || 0) + 1;
      playerGameCounts[defenderId] = (playerGameCounts[defenderId] || 0) + 1;

      // Increment win counts
      if (winnerId) {
        playerWinCounts[winnerId] = (playerWinCounts[winnerId] || 0) + 1;
      }

      // Increment challenge and defense counts
      playerChallengeCounts[challengerId] = (playerChallengeCounts[challengerId] || 0) + 1;
      playerDefenseCounts[defenderId] = (playerDefenseCounts[defenderId] || 0) + 1;
    });

    const totalGamesPlayed = filteredGames.length;

    // Create list of players with stats
    const playerStatistics: GameStatisticsPlayerResult[] = Object.values(playersMap).map((player) => {
      const gamesPlayed = playerGameCounts[player.id] || 0;
      const gamesWon = playerWinCounts[player.id] || 0;
      const winPercentage = gamesPlayed ? gamesWon / gamesPlayed : 0;
      const numberOfChallenges = playerChallengeCounts[player.id] || 0; // Challenges
      const numberOfDefenses = playerDefenseCounts[player.id] || 0; // Defenses

      return {
        player,
        gamesPlayed: { value: gamesPlayed, rank: 0, unit: 'number' },
        gamesWon: { value: gamesWon, rank: 0, unit: 'number' },
        winPercentage: { value: winPercentage, rank: 0, unit: 'percentage' },
        numberOfChallenges: {
          value: numberOfChallenges,
          rank: 0,
          unit: 'number',
        }, // Challenges
        numberOfDefenses: { value: numberOfDefenses, rank: 0, unit: 'number' }, // Defenses
      };
    });

    // Function to rank a list of player stats
    const rankPlayers = (statKey: keyof Omit<GameStatisticsPlayerResult, 'player'>) => {
      playerStatistics.sort((a, b) => b[statKey].value - a[statKey].value);
      let currentRank = 1;
      playerStatistics.forEach((stat, index) => {
        if (index > 0 && stat[statKey].value === playerStatistics[index - 1][statKey].value) {
          stat[statKey].rank = playerStatistics[index - 1][statKey].rank; // same rank for ties
        } else {
          stat[statKey].rank = currentRank;
        }
        currentRank++;
      });
    };

    // Rank players by each statistic
    rankPlayers('gamesPlayed');
    rankPlayers('gamesWon');
    rankPlayers('winPercentage');
    rankPlayers('numberOfChallenges'); // New ranking
    rankPlayers('numberOfDefenses'); // New ranking

    return {
      playerStatistics,
      totalGamesPlayed,
    };
  }, [games, fromDate, toDate]);
};
